import React, { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Target, Lightbulb, Trophy, ArrowRight } from 'lucide-react';

const caseStudies = [
  {
    id: 1,
    title: 'Enterprise Kubernetes Security Transformation',
    client: 'Fortune 500 Financial Services',
    duration: '6 months',
    color: '#10B981',
    challenge: 'Legacy security practices with manual compliance checks, inconsistent pod security across 50+ clusters, and no centralized policy enforcement.',
    solution: 'Implemented comprehensive Kubernetes security framework with OPA Gatekeeper, Pod Security Standards, and automated compliance scanning. Deployed centralized policy management across all clusters.',
    results: [
      '85% reduction in security vulnerabilities',
      'Zero critical CVEs in production',
      'Automated compliance reporting (SOC2, PCI-DSS)',
      'Reduced security audit time from weeks to hours'
    ],
    technologies: ['Kubernetes', 'OPA Gatekeeper', 'Trivy', 'Falco', 'Vault']
  },
  {
    id: 2,
    title: 'AWS to Azure Multi-Cloud Migration',
    client: 'Global E-commerce Platform',
    duration: '8 months',
    color: '#3B82F6',
    challenge: 'Single cloud vendor lock-in with AWS, growing costs, and need for geographic expansion into regions better served by Azure.',
    solution: 'Designed and executed phased migration strategy with zero downtime. Implemented Terraform-based multi-cloud infrastructure with unified deployment pipelines.',
    results: [
      'Zero downtime during migration',
      '40% reduction in cloud costs',
      'Multi-region deployment capability',
      '99.99% uptime maintained throughout'
    ],
    technologies: ['Terraform', 'AWS EKS', 'Azure AKS', 'ArgoCD', 'GitHub Actions']
  },
  {
    id: 3,
    title: 'GitOps-Driven Deployment Transformation',
    client: 'SaaS Technology Startup',
    duration: '4 months',
    color: '#8B5CF6',
    challenge: 'Manual deployments taking hours, inconsistent environments between staging and production, frequent deployment failures.',
    solution: 'Implemented complete GitOps workflow with ArgoCD, automated testing pipelines, and infrastructure-as-code for environment parity.',
    results: [
      '70% reduction in deployment time',
      '100% environment consistency',
      'Zero failed deployments post-implementation',
      'Developer productivity increased 3x'
    ],
    technologies: ['ArgoCD', 'GitHub Actions', 'Helm', 'Kubernetes', 'Terraform']
  },
  {
    id: 4,
    title: 'Zero-Trust Security Architecture Implementation',
    client: 'Healthcare Technology Provider',
    duration: '5 months',
    color: '#EC4899',
    challenge: 'HIPAA compliance requirements, legacy perimeter-based security model, and need for secure access to sensitive patient data across distributed teams.',
    solution: 'Designed and implemented zero-trust architecture with identity-based access, microsegmentation, and continuous verification. Integrated with existing identity providers.',
    results: [
      'Achieved HIPAA compliance certification',
      'Zero security breaches post-implementation',
      'Reduced attack surface by 90%',
      'Automated audit trail generation'
    ],
    technologies: ['Vault', 'Istio', 'OPA', 'Kubernetes RBAC', 'Cert-Manager']
  }
];

const CaseStudyAccordion = ({ study, isOpen, onToggle }) => {
  return (
    <div 
      className={`bg-white rounded-2xl shadow-md border-l-4 overflow-hidden transition-all duration-300 ${isOpen ? 'shadow-lg' : 'hover:shadow-lg'}`}
      style={{ borderLeftColor: study.color }}
      data-testid={`case-study-${study.id}`}
    >
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-start justify-between text-left hover:bg-gray-50 transition-colors"
        data-testid={`case-study-toggle-${study.id}`}
      >
        <div className="flex-1 pr-4">
          <div className="flex items-center gap-2 mb-2">
            <span 
              className="px-2.5 py-1 text-xs font-semibold rounded-full"
              style={{ backgroundColor: `${study.color}15`, color: study.color }}
            >
              {study.client}
            </span>
            <span className="text-gray-400 text-xs">{study.duration}</span>
          </div>
          <h3 className="font-bold text-gray-800 text-lg leading-tight">
            {study.title}
          </h3>
        </div>
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
          style={{ backgroundColor: isOpen ? `${study.color}15` : '#f3f4f6' }}
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5" style={{ color: study.color }} />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-6 pb-6 space-y-5 border-t border-gray-100 pt-5">
          {/* Challenge */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-red-500" />
              <span className="font-semibold text-gray-800 text-sm">Challenge</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed pl-6">
              {study.challenge}
            </p>
          </div>

          {/* Solution */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <span className="font-semibold text-gray-800 text-sm">Solution</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed pl-6">
              {study.solution}
            </p>
          </div>

          {/* Results */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-4 h-4 text-emerald-500" />
              <span className="font-semibold text-gray-800 text-sm">Results</span>
            </div>
            <div className="pl-6 space-y-1.5">
              {study.results.map((result, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" />
                  <span>{result}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Technologies */}
          <div className="pt-2">
            <div className="flex flex-wrap gap-2">
              {study.technologies.map((tech, i) => (
                <span 
                  key={i}
                  className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CaseStudiesSection = () => {
  const [openId, setOpenId] = useState(1); // First one open by default

  const handleToggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden" data-testid="case-studies-section">
      {/* Background decorations */}
      <div className="absolute top-10 right-10 text-6xl opacity-[0.03]">📋</div>
      <div className="absolute bottom-10 left-10 text-6xl opacity-[0.03]">🏆</div>
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 mb-4">
            <BookOpen className="w-4 h-4 text-purple-600" />
            <span className="font-bold text-purple-600 text-sm">Real-World Impact</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-800 mb-3">
            Advanced Case{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Studies
            </span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Deep dives into complex DevSecOps challenges and the solutions that delivered measurable results
          </p>
        </div>

        {/* Case Studies Accordion */}
        <div className="space-y-4">
          {caseStudies.map((study) => (
            <CaseStudyAccordion 
              key={study.id} 
              study={study} 
              isOpen={openId === study.id}
              onToggle={() => handleToggle(study.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
